import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FieldTypeOption,
  FormEditorInitialData,
} from 'src/blocks/form-editor-dialog';

import { useIsModuleEnabledLazy } from '@/hooks/useIsModuleEnabled';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import { fieldTypesConfig } from '../custom-attributes/field-types';
import { useRiskSmartForm } from './customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { useFormFieldElementsStore } from './stores/useFormFieldElementsStore';
import { useFormFieldRegistryStore } from './stores/useFormFieldRegistryStore';

const RISK_RATING_FORM_IDS: FormId[] = [
  'controlled_risk_assessment_result',
  'uncontrolled_risk_assessment_result',
  'risk_controlled_internal_audit_result',
  'risk_uncontrolled_internal_audit_result',
  'risk_controlled_second_line_result',
  'risk_uncontrolled_second_line_result',
];

const GROUP_DISPLAY_NAMES: Record<string, string> = {
  tierAndParent: 'Tier & Parent',
  testConfigFields: 'Test configuration',
};

const GROUP_FIELD_MAPPINGS: Record<string, string[]> = {
  tierAndParent: ['Tier', 'ParentRiskId'],
};

const getGroupDisplayName = (groupFieldId: string): string =>
  GROUP_DISPLAY_NAMES[groupFieldId] ??
  groupFieldId.replace(/([A-Z])/g, ' $1').trim();

const getGroupSubtitle = (
  groupFieldId: string,
  formConfig: Record<string, { fieldId: string; formLabel: string }> | undefined
): string => {
  const constituentIds = GROUP_FIELD_MAPPINGS[groupFieldId];
  if (constituentIds && constituentIds.length > 0 && formConfig) {
    return constituentIds
      .map((id) => {
        const f = Object.values(formConfig).find((fc) => fc.fieldId === id);

        return f?.formLabel ?? id;
      })
      .join(', ');
  }

  if (groupFieldId === 'testConfigFields') {
    return 'Test schedule configuration';
  }

  return '';
};

/**
 * Maps a custom attribute type to the form editor's field type.
 * Since the editor now uses CustomAttributeFieldType values directly,
 * this is a pass-through for all known types.
 */
const mapCustomAttributeType = (type: string): string => type;

const mapDisplayTypeToFieldType = (
  registryField: FormFieldConfig | undefined
): string => {
  if (!registryField) {
    return '';
  }
  if (!registryField.displayType) {
    return 'text';
  }

  const { displayType } = registryField.displayType;

  switch (displayType) {
    case 'commonLookup':
    case 'rating':
      return 'dropdown';
    case 'users':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'usermultiselect';
    case 'departments':
      return 'departmentmultiselect';
    case 'tags':
    case 'badgeList':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'dropdown'
        : 'multiselect';
    case 'textarea':
      return 'textarea';
    case 'date':
      return 'date';
    case 'number':
      return 'number';
    default:
      return '';
  }
};

interface UseFormEditorInitialDataResult {
  initialData: FormEditorInitialData;
  fieldTypeOptions: FieldTypeOption[];
}

/**
 * Builds the initialData and fieldTypeOptions needed by the FormEditorDialog.
 *
 * Extracts the data transformation logic that was previously inline in FormMenu,
 * reading from the form data context, field registry, and form config registry.
 */
export const useFormEditorInitialData = (
  parentType: FormId
): UseFormEditorInitialDataResult => {
  const { forcedRequiredFields, defaultRequiredFields } = useRiskSmartForm();
  const {
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    customAttributeSchema,
  } = useCustomisableFormDataContext();
  const registeredFields = useFormFieldRegistryStore(
    (state) => state.getAllFields
  );
  const currentFormElements = useFormFieldElementsStore(
    (state) => state.elements
  );
  const formConfigRegistry = useFormConfigRegistry();
  const formConfig = formConfigRegistry[parentType];
  const { t } = useTranslation(['common']);
  const isModuleEnabled = useIsModuleEnabledLazy();

  const initialData = useMemo((): FormEditorInitialData => {
    const sortedSections = [...(formSections ?? [])].sort(
      (a, b) => (a.ordering?.Position ?? 0) - (b.ordering?.Position ?? 0)
    );

    const orderByPosition = (fieldIds: string[]) =>
      [...fieldIds].sort((a, b) => {
        const aPos =
          formFieldOrdering?.find((o) => o.FieldId === a)?.Position ?? 0;
        const bPos =
          formFieldOrdering?.find((o) => o.FieldId === b)?.Position ?? 0;

        return aPos - bPos;
      });

    // When no sections are configured in the DB, create a default "Details"
    // section containing the current form's fields — matching useSectionManager.
    // Uses the elements store (scoped to the active CustomisableFieldWrapper)
    // rather than the global field registry to avoid including fields from
    // other mounted forms.
    const sections =
      sortedSections.length === 0
        ? [
            {
              id: 'section_details',
              name: 'Details',
              fieldIds: orderByPosition(currentFormElements.map((e) => e.key)),
            },
          ]
        : sortedSections.map((s) => ({
            id: s.SectionId,
            name: s.Label,
            fieldIds: orderByPosition((s.fields ?? []).map((f) => f.FieldId)),
          }));

    // Build a lookup from custom attribute schema for custom field labels/types
    const customFieldRenderProps = customAttributeSchema
      ? getCustomAttributeRenderProps(
          customAttributeSchema.Schema,
          customAttributeSchema.UiSchema
        )
      : [];
    const customFieldByScope = new Map(
      customFieldRenderProps.map((rp) => [rp.scope, rp])
    );

    const isRiskRatingForm = RISK_RATING_FORM_IDS.includes(parentType);
    const fields: FormEditorInitialData['fields'] = {};
    for (const reg of registeredFields()) {
      const config = formFieldConfigurations?.find(
        (fc) => fc.FieldId === reg.fieldId
      );
      const registryField = formConfig
        ? Object.values(formConfig).find(
            (f) => f.fieldId.toLowerCase() === reg.fieldId.toLowerCase()
          )
        : undefined;

      const isGroup = !reg.isCustom && !registryField;
      const customRenderProp = reg.isCustom
        ? customFieldByScope.get(reg.fieldId)
        : undefined;

      const fieldIdLower = reg.fieldId.toLowerCase();
      const isForcedRequired = forcedRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );
      const isDefaultRequired = defaultRequiredFields.some(
        (f) => f.toLowerCase() === fieldIdLower
      );

      // Resolve display name: custom field label from schema, config override,
      // static registry label, group name, or fallback to fieldId
      const resolvedName = isGroup
        ? getGroupDisplayName(reg.fieldId)
        : (config?.Label ??
          customRenderProp?.label ??
          registryField?.formLabel ??
          reg.fieldId);

      // Map custom attribute options to the editor's { id, label } format
      const editorOptions =
        customRenderProp?.options?.map((opt) => ({
          id:
            opt._tag === 'AltValueOption'
              ? (opt as { AltValue: string }).AltValue
              : opt.Value,
          label: opt.Value,
        })) ?? [];

      fields[reg.fieldId] = {
        name: resolvedName,
        type: customRenderProp
          ? mapCustomAttributeType(customRenderProp.type)
          : mapDisplayTypeToFieldType(registryField),
        required: config?.Required ?? (isForcedRequired || isDefaultRequired),
        readOnly: config?.ReadOnly ?? false,
        isCustom: reg.isCustom,
        forcedRequired: isForcedRequired,
        canDelete: reg.isCustom,
        canEditLabel:
          reg.isCustom ||
          (!isRiskRatingForm && !reg.fieldId.includes('schedule.')),
        canEditOptions: !isForcedRequired,
        subtitle: isGroup
          ? getGroupSubtitle(reg.fieldId, formConfig)
          : undefined,
        isGroup,
        ...(editorOptions.length > 0
          ? {
              config: {
                fieldType: mapCustomAttributeType(customRenderProp!.type),
                fieldName: resolvedName,
                required:
                  config?.Required ?? (isForcedRequired || isDefaultRequired),
                readOnly: config?.ReadOnly ?? false,
                hidden: config?.Hidden ?? false,
                defaultValueEnabled: false,
                defaultValue: '',
                options: editorOptions,
                conditionalLogicEnabled: false,
                conditionalLogicRules: [],
                guidanceEnabled: false,
                guidance: '',
              },
            }
          : {}),
      };
    }

    // Append custom attribute fields not already mapped to a section,
    // matching useSectionManager behaviour for unmapped custom fields
    if (customAttributeSchema && sections.length > 0) {
      const mappedFieldIds = new Set(sections.flatMap((s) => s.fieldIds));
      const renderProps = getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      );
      const unmappedCustomFieldIds: string[] = [];
      for (const rp of renderProps) {
        if (!mappedFieldIds.has(rp.scope) && fields[rp.scope]) {
          unmappedCustomFieldIds.push(rp.scope);
        }
      }
      if (unmappedCustomFieldIds.length > 0) {
        sections[sections.length - 1].fieldIds.push(...unmappedCustomFieldIds);
      }
    }

    return { sections, fields };
  }, [
    formSections,
    formFieldConfigurations,
    formFieldOrdering,
    registeredFields,
    currentFormElements,
    formConfig,
    parentType,
    forcedRequiredFields,
    defaultRequiredFields,
    customAttributeSchema,
  ]);

  const fieldTypeOptions = useMemo(
    (): FieldTypeOption[] =>
      Object.entries(fieldTypesConfig)
        .filter(
          ([, config]) =>
            !config.hasPermission || config.hasPermission(isModuleEnabled)
        )
        .map(([type, config]) => ({
          value: type,
          label: config.i18nKey ? (t(config.i18nKey) as string) : type,
        })),
    [isModuleEnabled, t]
  );

  return { initialData, fieldTypeOptions };
};
